import { useEffect, useRef } from 'react'
import { useGoogleOAuth } from '@react-oauth/google'
import { statusCodes } from '@react-native-google-signin/google-signin'
import type { GoogleWebAuthMode, GoogleWebAuthResult } from './useGoogleWebSignIn'

type PendingGoogleAuthRequest = {
  mode: GoogleWebAuthMode
  resolve: (result: GoogleWebAuthResult) => void
  reject: (error: unknown) => void
}

type GoogleIdTokenClaims = {
  given_name?: string
  family_name?: string
  name?: string
}

type GoogleCredentialResponse = {
  credential?: string
}

type GoogleButtonElement = {
  setAttribute: (name: string, value: string) => void
  style: Record<string, string>
  innerHTML: string
  querySelector?: (selector: string) => GoogleButtonElement | null
  firstElementChild?: GoogleButtonElement | null
  click: () => void
  remove?: () => void
}

type GoogleAccountsId = {
  initialize: (config: {
    client_id: string
    callback: (response: GoogleCredentialResponse) => void
    context?: 'signin' | 'signup' | 'use'
    ux_mode?: 'popup' | 'redirect'
    auto_select?: boolean
  }) => void
  renderButton: (
    parent: GoogleButtonElement,
    options: {
      type?: 'standard' | 'icon'
      theme?: 'outline' | 'filled_blue' | 'filled_black'
      size?: 'large' | 'medium' | 'small'
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
      shape?: 'rectangular' | 'pill' | 'circle' | 'square'
      logo_alignment?: 'left' | 'center'
      width?: number
      click_listener?: () => void
    },
  ) => void
}

type GoogleIdentityWindow = {
  accounts?: {
    id?: GoogleAccountsId
  }
}

type BrowserLikeGlobal = typeof globalThis & {
  google?: GoogleIdentityWindow
  document?: {
    createElement: (tag: string) => GoogleButtonElement
    body?: {
      appendChild: (element: GoogleButtonElement) => void
    }
  }
}

const browserGlobal = globalThis as BrowserLikeGlobal

const base64UrlDecode = (value: string) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
  const binary = atob(padded)

  // Convert base64 bytes into a UTF-8 string before JSON.parse.
  const percentEncoded = Array.from(binary)
    .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
    .join('')

  return decodeURIComponent(percentEncoded)
}

const decodeIdTokenClaims = (idToken: string): GoogleIdTokenClaims => {
  try {
    const payload = idToken.split('.')[1]
    if (!payload) {
      return {}
    }

    return JSON.parse(base64UrlDecode(payload)) as GoogleIdTokenClaims
  } catch {
    return {}
  }
}

const parseUserName = (claims: GoogleIdTokenClaims) => {
  if (claims.given_name && claims.family_name) {
    return { firstName: claims.given_name, lastName: claims.family_name }
  }

  const fullName = claims.name?.trim()
  if (!fullName) {
    return { firstName: undefined, lastName: undefined }
  }

  const [firstName, ...lastNameParts] = fullName.split(/\s+/)
  const lastName = lastNameParts.join(' ')

  return { firstName, lastName }
}

const GOOGLE_PROMPT_CANCELED_ERROR = { code: statusCodes.SIGN_IN_CANCELLED }
const GOOGLE_BUTTON_AUTH_TIMEOUT_MS = 30_000

export const useGoogleWebSignIn = () => {
  const pendingRequestRef = useRef<PendingGoogleAuthRequest | null>(null)
  const buttonContainerRef = useRef<GoogleButtonElement | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth()

  const clearAuthTimeout = () => {
    if (!timeoutRef.current) {
      return
    }

    clearTimeout(timeoutRef.current)
    timeoutRef.current = null
  }

  const rejectPendingRequest = (error: unknown) => {
    const pendingRequest = pendingRequestRef.current
    pendingRequestRef.current = null
    clearAuthTimeout()

    if (!pendingRequest) {
      return
    }

    pendingRequest.reject(error)
  }

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      const pendingRequest = pendingRequestRef.current
      pendingRequestRef.current = null
      pendingRequest?.reject(GOOGLE_PROMPT_CANCELED_ERROR)

      buttonContainerRef.current?.remove?.()
      buttonContainerRef.current = null
    },
    [],
  )

  const resolvePendingRequest = (response: GoogleCredentialResponse) => {
    const pendingRequest = pendingRequestRef.current
    pendingRequestRef.current = null
    clearAuthTimeout()

    if (!pendingRequest) {
      return
    }

    if (!response.credential) {
      pendingRequest.reject(new Error('Google Sign In did not return an ID token'))
      return
    }

    if (pendingRequest.mode === 'signIn') {
      pendingRequest.resolve({ providerToken: response.credential })
      return
    }

    const claims = decodeIdTokenClaims(response.credential)
    const { firstName, lastName } = parseUserName(claims)

    pendingRequest.resolve({
      providerToken: response.credential,
      firstName,
      lastName,
    })
  }

  const ensureButtonContainer = () => {
    const doc = browserGlobal.document
    if (!doc?.body) {
      throw new Error('Google Sign In is unavailable')
    }

    if (!buttonContainerRef.current) {
      const container = doc.createElement('div')
      container.setAttribute('aria-hidden', 'true')
      container.style.position = 'fixed'
      container.style.left = '-10000px'
      container.style.top = '0'
      container.style.width = '1px'
      container.style.height = '1px'
      container.style.overflow = 'hidden'
      container.style.opacity = '0'
      doc.body.appendChild(container)
      buttonContainerRef.current = container
    }

    return buttonContainerRef.current
  }

  const renderGoogleButton = (
    googleAccountsId: GoogleAccountsId,
    resolvedClientId: string,
    mode: GoogleWebAuthMode,
  ) => {
    const buttonContainer = ensureButtonContainer()

    googleAccountsId.initialize({
      client_id: resolvedClientId,
      callback: resolvePendingRequest,
      context: mode === 'signUp' ? 'signup' : 'signin',
      ux_mode: 'popup',
      auto_select: false,
    })

    buttonContainer.innerHTML = ''

    googleAccountsId.renderButton(buttonContainer, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: mode === 'signUp' ? 'signup_with' : 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 240,
      click_listener: () => {
        if (__DEV__) {
          console.warn('[GoogleSignIn] GIS button clicked', { mode })
        }
      },
    })

    return buttonContainer.querySelector?.('[role="button"]') ?? buttonContainer.firstElementChild
  }

  const startGoogleWebSignIn = (mode: GoogleWebAuthMode): Promise<GoogleWebAuthResult> => {
    if (!scriptLoadedSuccessfully) {
      return Promise.reject(new Error('Google Sign In is not ready yet'))
    }

    const resolvedClientId = clientId
    if (!resolvedClientId) {
      return Promise.reject(new Error('Google Sign In is not configured'))
    }

    const googleAccountsId = browserGlobal.google?.accounts?.id
    if (!googleAccountsId) {
      return Promise.reject(new Error('Google Sign In is unavailable'))
    }

    return new Promise((resolve, reject) => {
      if (pendingRequestRef.current) {
        reject(new Error('Google Sign In is already in progress'))
        return
      }

      let clickTarget: GoogleButtonElement | null | undefined
      try {
        clickTarget = renderGoogleButton(googleAccountsId, resolvedClientId, mode)
      } catch (setupError) {
        reject(setupError)
        return
      }

      if (!clickTarget) {
        reject(new Error('Google Sign In button failed to render'))
        return
      }

      pendingRequestRef.current = { mode, resolve, reject }
      timeoutRef.current = setTimeout(() => {
        console.warn('[GoogleSignIn] GIS button flow timed out', { mode })
        rejectPendingRequest(GOOGLE_PROMPT_CANCELED_ERROR)
      }, GOOGLE_BUTTON_AUTH_TIMEOUT_MS)

      clickTarget.click()
    })
  }

  return { startGoogleWebSignIn }
}
